import {
  ChoiceInput,
  Dropdown,
  Flex,
  Input,
  TextToolTip,
  Toggle,
} from '@components';
import { useCountriesQuery } from '@features/api/appDataSlice';
import { Box } from '@mui/system';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  UpdateTaxDetailsFormData,
  UpdateTaxDetailsProps,
} from './UpdateTaxDetails.types';
import {
  StyledButton,
  TinExcuseInput,
  ToggleBox,
} from './UpdateTaxDetails.styles';
import {
  useUpdateUserInfoMutation,
  useUserQuery,
} from '@features/api/userSlice';
import countriesJSON from '@assets/countries.json';
import { updateTaxDetailsFormSchema } from '@validationSchemas/updateProfile';
import { yupResolver } from '@hookform/resolvers/yup';
import { getFlagEmoji } from '@utils/functions/getFlagFromCountryCode';
import { yesNoChoices } from '@utils/choiceValues/choiceValues';

const UpdateTaxDetails: React.FC<UpdateTaxDetailsProps> = ({ onClose }) => {
  const intl = useIntl();
  const { data: countries } = useCountriesQuery();
  const { data: user } = useUserQuery();
  const [updateUserInfoMutation] = useUpdateUserInfoMutation();
  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors, isValid, dirtyFields },
    clearErrors,
    setValue,
  } = useForm<UpdateTaxDetailsFormData>({
    resolver: yupResolver(updateTaxDetailsFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      primaryTaxResidenceCountry: user?.taxDetails?.taxResidenceCountry ?? '',
      primaryTin: user?.taxDetails?.primaryTin ?? '',
      secondaryTaxResidenceCountry:
        user?.taxDetails?.secondaryTaxResidenceCountry ?? '',
      secondaryTin: user?.taxDetails?.secondaryTin ?? '',
      usTaxPerson:
        user?.taxDetails?.usTaxPayer !== undefined
          ? user?.taxDetails?.usTaxPayer
          : undefined,
      noPrimaryIdentificationNumber: !!user?.taxDetails?.noPrimaryTinReason,
      primaryTinExcuse: user?.taxDetails?.noPrimaryTinReason,
      noSecondaryIdentificationNumber: !!user?.taxDetails?.noSecondaryTinReason,
      secondaryTinExcuse: user?.taxDetails?.noSecondaryTinReason,
    },
  });

  const primaryTaxResidenceCountry = watch('primaryTaxResidenceCountry');
  const primaryTin = watch('primaryTin');
  const secondaryTaxResidenceCountry = watch('secondaryTaxResidenceCountry');
  const secondaryTin = watch('secondaryTin');

  const noPrimaryIdentificationNumber = watch('noPrimaryIdentificationNumber');
  const noSecondaryIdentificationNumber = watch(
    'noSecondaryIdentificationNumber'
  );

  useEffect(() => {
    if (noPrimaryIdentificationNumber) {
      setValue('primaryTin', '');
      trigger('primaryTin');
      clearErrors('primaryTin');
    } else {
      setValue('primaryTinExcuse', '');
      trigger('primaryTinExcuse');
      clearErrors('primaryTinExcuse');
    }

    if (noSecondaryIdentificationNumber) {
      setValue('secondaryTin', '');
      trigger('secondaryTin');
      clearErrors('secondaryTin');
    } else {
      setValue('secondaryTinExcuse', '');
      trigger('secondaryTinExcuse');
      clearErrors('secondaryTinExcuse');
    }
  }, [
    noPrimaryIdentificationNumber,
    setValue,
    clearErrors,
    trigger,
    noSecondaryIdentificationNumber,
  ]);

  useEffect(() => {
    if (primaryTaxResidenceCountry !== undefined) {
      trigger('primaryTin');
    }
    if (primaryTin !== undefined) {
      trigger('primaryTaxResidenceCountry');
    }
    if (secondaryTaxResidenceCountry !== undefined) {
      trigger('secondaryTin');
    }
    if (secondaryTin !== undefined) {
      trigger('secondaryTaxResidenceCountry');
    }
  }, [
    primaryTaxResidenceCountry,
    primaryTin,
    secondaryTaxResidenceCountry,
    secondaryTin,
    trigger,
  ]);

  const countriesList = useMemo(() => {
    const countriesArray = countriesJSON as Array<any>;
    return (
      countriesArray.map((x) => ({
        name: x.name,
        value: x.name,
        icon: getFlagEmoji(x.code),
      })) ?? []
    );
  }, []);

  const allowedCountriesList = useMemo(() => {
    return (
      countries?.map((x) => ({
        name: x.name,
        value: x.name,
        icon: x.flag,
      })) ?? []
    );
  }, [countries]);

  const onSubmit = async ({
    primaryTaxResidenceCountry,
    primaryTin,
    secondaryTaxResidenceCountry,
    secondaryTin,
    usTaxPerson,
    noPrimaryIdentificationNumber,
    primaryTinExcuse,
    noSecondaryIdentificationNumber,
    secondaryTinExcuse,
  }: UpdateTaxDetailsFormData) => {
    if (!user) return;

    try {
      await updateUserInfoMutation({
        ...user,
        taxDetails: {
          ...user.taxDetails,
          taxResidenceCountry: primaryTaxResidenceCountry ?? undefined,
          primaryTin: !noPrimaryIdentificationNumber ? primaryTin : undefined,
          secondaryTaxResidenceCountry,
          secondaryTin: !noSecondaryIdentificationNumber
            ? secondaryTin
            : undefined,
          usTaxPayer: usTaxPerson,
          noPrimaryTinReason: noPrimaryIdentificationNumber
            ? primaryTinExcuse
            : undefined,
          noSecondaryTinReason: noSecondaryIdentificationNumber
            ? secondaryTinExcuse
            : undefined,
        },
      }).unwrap();

      onClose();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex flexDirection='column' mt={12} gap={16}>
        <Box>
          <Controller
            control={control}
            name='primaryTaxResidenceCountry'
            render={({ field }) => (
              <Dropdown
                label={intl.formatMessage({
                  id: 'investment.taxDetails.taxResidence',
                })}
                inputProps={{
                  'data-testid': 'tax-residence',
                }}
                menuItemData={allowedCountriesList}
                {...getErrorProps(intl, errors.primaryTaxResidenceCountry)}
                {...field}
              />
            )}
          />
        </Box>
        <Box>
          <Input
            label={intl.formatMessage({
              id: 'investment.taxDetails.taxpayerIdentificationNumber',
            })}
            fullWidth
            disabled={noPrimaryIdentificationNumber}
            inputProps={{
              'data-testid': 'primary-tin',
            }}
            {...registerInput(register, 'primaryTin')}
            {...getErrorProps(intl, errors.primaryTin)}
          />
          <ToggleBox>
            <Controller
              control={control}
              name='noPrimaryIdentificationNumber'
              render={({ field }) => (
                <Toggle
                  label={intl.formatMessage({
                    id: 'investment.taxDetails.notHaveTaxpayerIdentificationNumber',
                  })}
                  labelPlacement='end'
                  labelProps={{
                    variant: 'h6',
                    fontWeight: 'fontWeightMedium',
                  }}
                  {...field}
                  checked={field.value}
                />
              )}
            />
          </ToggleBox>
        </Box>
        <TinExcuseInput
          label={intl.formatMessage({
            id: 'investment.taxDetails.reasonForNonAvailability',
          })}
          fullWidth
          sx={{
            display: noPrimaryIdentificationNumber ? 'initial' : 'none',
          }}
          multiline
          inputProps={{
            'data-testid': 'primary-tin-excuse',
            sx: { pl: 5, pt: { xs: 8, lg: 6, xl: 3 } },
          }}
          {...registerInput(register, 'primaryTinExcuse')}
          {...getErrorProps(intl, errors.primaryTinExcuse)}
        />
        <Box>
          <Controller
            control={control}
            name='secondaryTaxResidenceCountry'
            render={({ field }) => (
              <Dropdown
                label={intl.formatMessage({
                  id: 'profile.taxResidenceSecondary',
                })}
                inputProps={{
                  'data-testid': 'tax-residence-secondary',
                }}
                menuItemData={countriesList}
                {...getErrorProps(intl, errors.secondaryTaxResidenceCountry)}
                {...field}
              />
            )}
          />
        </Box>
        <Box>
          <Input
            label={intl.formatMessage({
              id: 'profile.taxIdentificationNumberSecondary',
            })}
            fullWidth
            disabled={noSecondaryIdentificationNumber}
            inputProps={{
              'data-testid': 'secondary-tin',
            }}
            {...registerInput(register, 'secondaryTin')}
            {...getErrorProps(intl, errors.secondaryTin)}
          />
          <ToggleBox>
            <Controller
              control={control}
              name='noSecondaryIdentificationNumber'
              render={({ field }) => (
                <Toggle
                  label={intl.formatMessage({
                    id: 'investment.taxDetails.notHaveTaxpayerIdentificationNumber',
                  })}
                  labelPlacement='end'
                  labelProps={{
                    variant: 'h6',
                    fontWeight: 'fontWeightMedium',
                  }}
                  {...field}
                  checked={field.value}
                />
              )}
            />
          </ToggleBox>
        </Box>
        <TinExcuseInput
          label={intl.formatMessage({
            id: 'investment.taxDetails.reasonForNonAvailability',
          })}
          fullWidth
          sx={{
            display: noSecondaryIdentificationNumber ? 'initial' : 'none',
          }}
          multiline
          inputProps={{
            'data-testid': 'secondary-tin-excuse',
            sx: { pl: 5, pt: { xs: 8, lg: 6, xl: 3 } },
          }}
          {...registerInput(register, 'secondaryTinExcuse')}
          {...getErrorProps(intl, errors.secondaryTinExcuse)}
        />

        <Flex flexDirection='column'>
          <TextToolTip
            text={intl.formatMessage({
              id: 'investment.taxDetails.usTaxPurposes',
            })}
            toolTipText={intl.formatMessage({
              id: 'investment.taxDetails.usPersonTooltipMessage',
            })}
          />
          <Controller
            control={control}
            name='usTaxPerson'
            render={({ field: { ref, value, ...restProps } }) => (
              <ChoiceInput
                direction='row'
                fullWidth
                choices={yesNoChoices}
                defaultValues={value !== undefined ? [value] : undefined}
                {...restProps}
              />
            )}
          />
        </Flex>

        <StyledButton
          disabled={!isValid || Object.keys(dirtyFields).length === 0}
          type='submit'
          data-testid='tax-save-button'
        >
          <FormattedMessage id='misc.save' />
        </StyledButton>
      </Flex>
    </form>
  );
};

export default UpdateTaxDetails;
